import { useMemo, useCallback } from 'react'
import { noop } from '@utils'
import cn from 'clsx'

import { Dialog as HeadlessDialog } from '@headlessui/react'

// @todo subtitle could replace description
const Dialog = ({
    className,
    backdropClassName,
    containerClassName,
    panelClassName,
    open = false,
    setOpen,
    modal = false,
    title = null,
    subtitle = null,
    children,
    footers = [],
    backdropBlur = true,
    
    positiveText = 'OK',
    onPositiveClick,
    positiveLoading,
    positiveDisabled,
    
    negativeText = 'Cancel',
    onNegativeClick,
    negativeLoading,
    negativeDisabled,
    
    dialogProps = {},
    backdropProps = {},
    containerProps = {},
    panelProps = {},
}) => {
    
    const backdropStyles = useMemo(() => {
        
        if (backdropBlur) return {}
        
        return {
            backdropFilter: 'none',
        }
        
    }, [backdropBlur])
    
    const handleClick = useCallback(fn => {
        
        return async () => {
            
            try {
                await fn()
            } catch (e) {
                console.error('Dialog', 'handleClick', e)
            }
            
            setOpen(false)
            
        }
        
    }, [setOpen])
    
    return (
        
        <HeadlessDialog
            className={cn('Dialog', className)}
            open={open}
            onClose={() => !modal && setOpen(false)}
            {...dialogProps}>
            
            {/* The backdrop, rendered as a fixed sibling to the panel container */}
            <div
                className={cn('z-[81] modal-backdrop fixed inset-0 !bg-black/50 ', backdropClassName)}
                style={backdropStyles}
                aria-hidden="true"
                {...backdropProps} />
            
            {/* Full-screen container to center the panel */}
            <div
                className={cn('z-[302] fixed inset-0 flex w-screen items-center justify-center p-4 ',
                    containerClassName)}
                {...containerProps}>
                
                {/* The actual dialog panel  */}
                <HeadlessDialog.Panel
                    className={cn('flex w-2/3 flex-col modal-box rounded', panelClassName)} 
                    {...panelProps}>
                    
                    {title && (
                        <HeadlessDialog.Title>
                            {typeof title === 'string' ? (
                                <h3 className={cn({
                                    'mb-0': subtitle?.length > 0,
                                })}>
                                    {title}
                                </h3>
                            ) : title}
                        </HeadlessDialog.Title>
                    )}
                    
                    {subtitle?.length > 0 && (
                        <HeadlessDialog.Description>
                            {subtitle}
                        </HeadlessDialog.Description>
                    )}
                    
                    <div className="body my-4 text-base grow overflow-auto">
                        {children}
                    </div>
                    
                    <footer className={cn('flex items-center content-center gap-3', {
                        'justify-end': !footers?.length,
                        'justify-between': footers?.length > 0,
                    })}>
                        
                        {footers}
                        
                        {((negativeText && onNegativeClick) || positiveText) && (
                            
                            <div className="flex justify-end items-center content-center shrink-0 gap-2">
                                
                                {negativeText && onNegativeClick && (
                                    <button
                                        className="btn btn-secondary flex items-center content-center"
                                        disabled={negativeDisabled}
                                        onClick={handleClick(onNegativeClick)}>
                                        {negativeLoading && <span className="loading loading-spinner loading-xs" />}
                                        {negativeText}
                                    </button>
                                )}
                                
                                {positiveText && (
                                    <button
                                        className="btn btn-primary flex items-center content-center"
                                        disabled={positiveDisabled}
                                        onClick={handleClick(onPositiveClick || noop)}>
                                        {positiveLoading && <span className="loading loading-spinner loading-xs" />}
                                        {positiveText}
                                    </button>
                                )}
                            
                            </div>
                            
                        )}
                    
                    </footer>
                
                </HeadlessDialog.Panel>
            
            </div>
        
        </HeadlessDialog>
        
    )
    
}

export default Dialog
